import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";

// Types
type Product = {
  id: number;
  category: 'formal' | 'casual' | 'linen' | 'cotton';
  images: string[];
};

const AUTOPLAY_MS = 3000; // 3s per image

// ================= Product Card ==================
const ProductCard: React.FC<{
  id: number;
  images: string[];
  paused: boolean;
  setPaused: (v: boolean) => void;
}> = ({ id, images, paused, setPaused }) => {
  const [idx, setIdx] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);

  const current = images[idx] || images[0];
  const currentSrc = encodeURI(current);

  // Visibility check (mobile optimization)
  const [isVisible, setIsVisible] = useState(true);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.5 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => {
      if (cardRef.current) observer.unobserve(cardRef.current);
    };
  }, []);

  // Autoplay effect
  useEffect(() => {
    if (paused || !isVisible) return;
    const timer = setInterval(() => {
      setIdx((i) => (i + 1) % images.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [paused, isVisible, images.length]);

  // Manual navigation
  const goPrev = () => {
    setIdx((i) => (i - 1 + images.length) % images.length);
    setPaused(true);
  };
  const goNext = () => {
    setIdx((i) => (i + 1) % images.length);
    setPaused(true);
  };
  const goTo = (n: number) => {
    setIdx(n);
    setPaused(true);
  };

  return (
    <div
      ref={cardRef}
      className="relative bg-white rounded-2xl shadow-md border border-gray-200 p-4 overflow-visible"
    >
      {/* IMAGE BOX */}
      <div
        className="relative bg-white overflow-hidden h-[340px] md:h-[360px]"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
      >
        <AnimatePresence mode="wait">
          <motion.img
            key={currentSrc}
            src={currentSrc}
            alt="Product image"
            className="h-full w-full object-cover block select-none rounded-xl"
            draggable={false}
            loading="eager"
            decoding="async"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          />
        </AnimatePresence>
      </div>

      {/* CONTROLS */}
      {images.length > 1 && (
        <div className="flex items-center justify-center gap-3 pt-3">
          {/* prev */}
          <button
            onClick={goPrev}
            aria-label="Previous image"
            className="p-1 bg-transparent"
            title="Previous"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={() => setPaused(true)}
            onTouchEnd={() => setPaused(false)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" className="text-black" fill="none">
              <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* dots */}
          <div className="flex items-center gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                aria-label={`Go to image ${i + 1}`}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  i === idx ? 'bg-yellow-600 scale-110' : 'bg-gray-300 hover:bg-gray-400'
                }`}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                onTouchStart={() => setPaused(true)}
                onTouchEnd={() => setPaused(false)}
              />
            ))}
          </div>

          {/* next */}
          <button
            onClick={goNext}
            aria-label="Next image"
            className="p-1 bg-transparent"
            title="Next"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onTouchStart={() => setPaused(true)}
            onTouchEnd={() => setPaused(false)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" className="text-black" fill="none">
              <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

// ================= Collection ==================
const Collection: React.FC = () => {
  const [paused, setPaused] = useState(false);

   const products: Product[] = useMemo(
    () => [
      { id: 1,  category: 'formal', images: [
          
          '/wetransfer_gani-shirt-collection/GG-05 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-05 B.jpg',
        '/wetransfer_gani-shirt-collection/GG-05 E.jpg',
        '/wetransfer_gani-shirt-collection/GG-05 C.jpg',
        '/wetransfer_gani-shirt-collection/GG-05 D.jpg',
      ]},
      { id: 2,  category: 'formal', images: [
        
        '/wetransfer_gani-shirt-collection/GG-06 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-06 B.jpg',
        '/wetransfer_gani-shirt-collection/GG-06 E.jpg',
        '/wetransfer_gani-shirt-collection/GG-06 C.jpg',
        '/wetransfer_gani-shirt-collection/GG-06 D.jpg',
      ]},
      { id: 3,  category: 'casual', images: [
        
        '/wetransfer_gani-shirt-collection/GG-09 E.jpg',
        '/wetransfer_gani-shirt-collection/GG-09 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-09 D.jpg',
        '/wetransfer_gani-shirt-collection/GG-09 B.jpg',
        '/wetransfer_gani-shirt-collection/GG-09 C.jpg',
        
      ]},
      { id: 4,  category: 'formal', images: [
        
        '/wetransfer_gani-shirt-collection/GG-08 B.jpg',
        '/wetransfer_gani-shirt-collection/GG-08 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-08 E.jpg',
        '/wetransfer_gani-shirt-collection/GG-08 C.jpg',
        '/wetransfer_gani-shirt-collection/GG-08 D.jpg',
      ]},
      { id: 5,  category: 'formal', images: [
          "/wetransfer_gani-shirt-collection/GG-01 A.jpg",
          "/wetransfer_gani-shirt-collection/GG-01 E.jpg",
          "/wetransfer_gani-shirt-collection/GG-01 D.jpg",
          "/wetransfer_gani-shirt-collection/GG-01 B.jpg",
          "/wetransfer_gani-shirt-collection/GG-01 C.jpg",
      ]},
      { id: 6,  category: 'formal', images: [
        '/wetransfer_gani-shirt-collection/GG-02 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-02 B.jpg',
        '/wetransfer_gani-shirt-collection/GG-02 D.jpg',
        '/wetransfer_gani-shirt-collection/GG-02 C.jpg',
        '/wetransfer_gani-shirt-collection/GG-02 E.jpg',
      ]},
      { id: 7,  category: 'formal', images: [
        '/wetransfer_gani-shirt-collection/GG-03 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-03 B.jpg',
        '/wetransfer_gani-shirt-collection/GG-03 C.jpg',
        '/wetransfer_gani-shirt-collection/GG-03 D.jpg',
        '/wetransfer_gani-shirt-collection/GG-03 E.jpg',
       
      ]},
      { id: 8,  category: 'formal', images: [
        '/wetransfer_gani-shirt-collection/GG-04 E.jpg',
        '/wetransfer_gani-shirt-collection/GG-04 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-04 C.jpg',
        '/wetransfer_gani-shirt-collection/GG-04 B.jpg',
        '/wetransfer_gani-shirt-collection/GG-04 D.jpg',
      ]},
      { id: 9,  category: 'formal', images: [
        '/wetransfer_gani-shirt-collection/GG-11 B.jpg',
        '/wetransfer_gani-shirt-collection/GG-11 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-11 C.jpg',
        '/wetransfer_gani-shirt-collection/GG-11 E.jpg',
        '/wetransfer_gani-shirt-collection/GG-11 D.jpg',
      ]},
      { id: 10, category: 'formal', images: [
        '/wetransfer_gani-shirt-collection/GG-10 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-10 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-10 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-10 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-10 A.jpg',
      ]},
      { id: 11, category: 'formal', images: [
        '/wetransfer_gani-shirt-collection/GG-12 B.jpg',
        '/wetransfer_gani-shirt-collection/GG-12 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-12 D.jpg',
        '/wetransfer_gani-shirt-collection/GG-12 E.jpg',
        '/wetransfer_gani-shirt-collection/GG-12 C.jpg',
      ]},
      { id: 12, category: 'formal', images: [
         '/wetransfer_gani-shirt-collection/GG-07 E.jpg',
        '/wetransfer_gani-shirt-collection/GG-07 A.jpg',
        '/wetransfer_gani-shirt-collection/GG-07 D.jpg',
        '/wetransfer_gani-shirt-collection/GG-07 B.jpg',
        '/wetransfer_gani-shirt-collection/GG-07 C.jpg',
      ]},
    ],
    []
  );


  return (
    <div className="min-h-screen bg-gray-50">
       {/* Header */}
      <section className="relative py-16 bg-gradient-to-r from-gray-900 to-gray-800 overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('/collection_banner_img1.jpg')" }}
          initial={{ scale: 1.2, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.2 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />

        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h1
            className="text-3xl md:text-4xl font-bold text-white mb-4"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Our Collection
          </motion.h1>
          <motion.p
            className="text-[14px] md:text-base  text-gray-300 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Discover our premium range of shirts crafted with the finest materials
          </motion.p>
        </div>
      </section>

<section className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="grid gap-6 sm:gap-7 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.15 } },
          }}
        >
          {products.map((p) => (
            <motion.div
              key={p.id}
              variants={{
                hidden: { opacity: 0, y: 40 },
                visible: { opacity: 1, y: 0 },
              }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <ProductCard
                id={p.id}
                images={p.images}
                paused={paused}
                setPaused={setPaused}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
      </section>

       {/* Optional CTA */}
      <section className="bg-yellow-600 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl  font-bold text-white mb-3"
            >
            Can't Find What You're Looking For?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            viewport={{ once: true }}
            className="text-[14px] md:text-base  text-yellow-100 mb-8"
          >
            Contact us for custom tailoring and special orders
          </motion.p>
          <motion.a
            href="tel:9884017119"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            viewport={{ once: true }}
          >
            <button className="bg-white text-yellow-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Contact Us
            </button>
          </motion.a>
        </div>
      </section>


    </div>
  );
};

export default Collection;
